import { confirmDialog } from '../../../ui/dialogs';
import { hasPerm } from '../../session';
import { conversationStudy } from '../context';
import { dateText, RIS_PERM, summaryApprovable } from './logic';
import { type ApprovedSummary, MAX_PATIENT_SUMMARY, type PatientSummaryDraft } from './shapes';
import { busy, button, el, loading, type RadiologistDeps, textBlock, toolCard, toolFailure, traceNodes } from './ui';

/**
 * "Resumen para el paciente" (contract v3 §2.9, off by default: patient-facing). The model writes a
 * plain-language summary of the signed report in force; the reader edits it in the card and only a
 * person who may sign reports approves it ("Aprobar", confirmed with the full text). Only the
 * approved text reaches the patient portal, and only when the report is deliverable.
 */

export const SUMMARY_TEXT = {
  tool: 'Resumen para el paciente',
  title: 'Resumen para el paciente',
  loading: 'cargando…',
  disclaimer: 'Texto para el paciente en lenguaje sencillo. Revíselo y edítelo: solo el texto aprobado se muestra en el portal del paciente.',
  approved: (at: string) => `Aprobado el ${at}:`,
  none: 'Aún no hay un resumen aprobado.',
  generate: 'Generar resumen',
  generating: 'Generando…',
  label: 'Resumen para el paciente (editable)',
  approve: 'Aprobar',
  approving: 'Aprobando…',
  approveTitle: 'Aprobar resumen para el paciente',
  approveIntro: 'El paciente verá exactamente este texto en el portal, junto a su informe:',
  confirm: 'Aprobar',
  cancel: 'Cancelar',
  done: 'Resumen aprobado.',
  signOnly: 'Solo quien puede firmar informes aprueba el resumen.',
  tooLong: `El resumen admite hasta ${MAX_PATIENT_SUMMARY} caracteres.`,
  empty: 'Escriba el resumen antes de aprobarlo.',
  noReport: 'El resumen no está asociado a un informe firmado.',
  failed: 'No fue posible generar el resumen.',
  loadFailed: 'No fue posible cargar el resumen aprobado.',
  approveFailed: 'No fue posible aprobar el resumen.',
} as const;

interface SummaryUi {
  approved: HTMLElement;
  editor: HTMLElement;
  generate: HTMLButtonElement;
}

function showApproved(ui: SummaryUi, approved: ApprovedSummary | null): void {
  ui.approved.replaceChildren(approved
    ? textBlock(SUMMARY_TEXT.approved(dateText(approved.approvedAt)), approved.text, 'assistant-summary-approved')
    : el('p', 'assistant-tool-note', SUMMARY_TEXT.none));
  ui.approved.dataset.approved = String(approved !== null);
}

function approveProblem(draft: PatientSummaryDraft, text: string): string | null {
  if (!draft.reportId) return SUMMARY_TEXT.noReport;
  if (!text.trim()) return SUMMARY_TEXT.empty;
  return summaryApprovable(text) ? null : SUMMARY_TEXT.tooLong;
}

async function approve(deps: RadiologistDeps, study: string, draft: PatientSummaryDraft, field: HTMLTextAreaElement, ui: SummaryUi & { trigger: HTMLButtonElement }): Promise<void> {
  const text = field.value.trim();
  const problem = approveProblem(draft, text);
  if (problem || !draft.reportId) return void deps.shell.toast(problem ?? SUMMARY_TEXT.noReport, 'warning');
  const reportId = draft.reportId;
  const confirmed = await confirmDialog(deps.shell, {
    title: SUMMARY_TEXT.approveTitle, text: `${SUMMARY_TEXT.approveIntro}\n\n${text}`,
    confirmLabel: SUMMARY_TEXT.confirm, cancelLabel: SUMMARY_TEXT.cancel, className: 'assistant-insert-dialog assistant-summary-dialog',
  });
  if (!confirmed) return;
  await busy(ui.trigger, SUMMARY_TEXT.approving, async () => {
    try {
      const approved = await deps.api.approvePatientSummary(study, reportId, text);
      showApproved(ui, approved ?? { text, approvedAt: null });
      ui.editor.replaceChildren();
      deps.shell.toast(SUMMARY_TEXT.done);
    } catch (error) {
      console.warn('[viewer] patient summary approve failed', error);
      deps.shell.toast(toolFailure(error, 'patient_summary', SUMMARY_TEXT.approveFailed), 'error');
    }
  });
}

function showDraft(deps: RadiologistDeps, study: string, draft: PatientSummaryDraft, ui: SummaryUi): void {
  const field = el('textarea', 'assistant-ask-input assistant-summary-input');
  field.rows = 8;
  field.maxLength = MAX_PATIENT_SUMMARY;
  field.value = draft.text;
  field.setAttribute('aria-label', SUMMARY_TEXT.label);
  const row = el('div', 'assistant-finding-actions');
  if (hasPerm(RIS_PERM.reportsSign)) {
    const trigger: HTMLButtonElement = button(SUMMARY_TEXT.approve, 'assistant-summary-approve', () => void approve(deps, study, draft, field, { ...ui, trigger }));
    row.appendChild(trigger);
  } else {
    row.appendChild(el('p', 'assistant-tool-note assistant-summary-sign-only', SUMMARY_TEXT.signOnly));
  }
  ui.editor.replaceChildren(el('label', 'assistant-ask-label', SUMMARY_TEXT.label), field, ...traceNodes({ model: draft.model, modelVersion: null, isTest: draft.isTest }), row);
  field.focus();
}

export async function showPatientSummary(deps: RadiologistDeps): Promise<void> {
  const study = conversationStudy(deps.shell);
  const { body } = toolCard(deps, 'patient-summary', SUMMARY_TEXT.title, study);
  const ui: SummaryUi = { approved: el('div', 'assistant-summary-state'), editor: el('div', 'assistant-summary-editor'), generate: button(SUMMARY_TEXT.generate, 'assistant-summary-generate', () => undefined) };
  ui.generate.addEventListener('click', () => void busy(ui.generate, SUMMARY_TEXT.generating, async () => {
    try {
      const draft = await deps.api.generatePatientSummary(study);
      if (draft.approved) showApproved(ui, draft.approved);
      showDraft(deps, study, draft, ui);
    } catch (error) {
      console.warn('[viewer] patient summary failed', error);
      deps.shell.toast(toolFailure(error, 'patient_summary', SUMMARY_TEXT.failed), 'error');
    }
  }));
  const actions = el('div', 'assistant-finding-actions');
  actions.appendChild(ui.generate);
  body.append(el('p', 'assistant-tool-note', SUMMARY_TEXT.disclaimer), ui.approved, actions, ui.editor);
  loading(ui.approved, SUMMARY_TEXT.loading);
  try {
    showApproved(ui, await deps.api.patientSummary(study));
  } catch (error) {
    console.warn('[viewer] patient summary load failed', error);
    ui.approved.replaceChildren(el('p', 'assistant-tool-note assistant-tool-error', toolFailure(error, 'patient_summary', SUMMARY_TEXT.loadFailed)));
  }
}
